// Copyright (c) StrangeBee 2024

import React, { ReactElement, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Avatar } from 'antd';
import { UserOutput } from 'thehive-sdk';
import { useLocalMessage, getAvatarUrl, handleNotificationError } from '@Helpers/';
import { THDropdown } from '@Molecules/Inputs/THDropdown/THDropdown';
import RouteBuilder from '@Services/RouteBuilder';
import { GlobalStreamLongPollingSrv, GlobalStreamSrv } from '@Services/StreamSrv';
import AboutSection from '../AboutSection/AboutSection';
import client, { queryClient } from '@Client';
import { ICONS } from '@Constants/iconsApp';
import { THMenu } from '@Molecules/THMenu/THMenu';
import ExternalLink from '@Atoms/ExternalLink/ExternalLink';

import './AccountMenu.scss';

interface AccountMenuProps {
    user?: UserOutput;
}

const AccountMenu = ({ user }: AccountMenuProps): ReactElement => {
    const i = useLocalMessage();
    const { name = '', avatar = '' } = user || {};
    const history = useHistory();

    const logout = useCallback(() => {
        client.AuthController.logout()
            .then(() => {
                queryClient.refetchQueries('currentUser');
                GlobalStreamSrv.cancel();
                GlobalStreamLongPollingSrv.cancelFeed();
            })
            .catch(handleNotificationError);
    }, []);

    return (
        <THDropdown
            triggerElem={() => (
                <div className="user-menu-button">
                    <div className="name">{name}</div>
                    <div className="avatar">
                        {avatar !== '' ? (
                            <Avatar src={getAvatarUrl(avatar)} size="default" className="user-button__avatar" />
                        ) : (
                            <Avatar icon={<ICONS.UserIcon />} size="default" className="user-button__avatar" />
                        )}
                        <ICONS.CaretDownOutlined className="user-menu-icon" />
                    </div>
                </div>
            )}
            menuClassName="account-menu__overlay"
            trigger={['click']}
            placement="bottom"
        >
            <THMenu.Item key="settings" onClick={() => history.push(RouteBuilder.compute('account.infos'))}>
                <ICONS.UserProfileIcon />
                {i('mainLayout.settings.account')}
            </THMenu.Item>

            <THMenu.Item key="api-docs">
                <ICONS.ReadOutlined />
                <ExternalLink
                    url={`${window.location.origin + client.apiUrl}/v1/docs`}
                    className="account-menu__api-docs"
                >
                    {i('general.documentation')}
                </ExternalLink>
            </THMenu.Item>

            <AboutSection
                triggerElem={
                    <THMenu.Item key="settings">
                        <ICONS.QuestionCircleOutlined />
                        {i('mainLayout.settings.about')}
                    </THMenu.Item>
                }
            />

            <THMenu.Item key="logout" onClick={() => logout()}>
                <ICONS.LogoutOutlined />
                {i('mainLayout.userMenu.logout')}
            </THMenu.Item>
        </THDropdown>
    );
};

export default AccountMenu;
